// src/core/models/json-value.ts
function isJSONObject(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

// src/core/models/ai-provider.ts
var AIProvider = {
  meta: "meta",
  anthropic: "anthropic",
  openai: "openai",
  gemini: "gemini",
  xai: "xai",
  perplexity: "perplexity",
  deepseek: "deepseek",
  zai: "zai",
  minimax: "minimax",
  mistral: "mistral",
  venice: "venice",
  openAICompatible: "openAICompatible",
  appleIntelligence: "appleIntelligence",
  mlx: "localMLX",
  aiEdge: "aiEdge"
};
var ALL_PROVIDERS = [
  AIProvider.meta,
  AIProvider.anthropic,
  AIProvider.openai,
  AIProvider.gemini,
  AIProvider.xai,
  AIProvider.perplexity,
  AIProvider.deepseek,
  AIProvider.zai,
  AIProvider.minimax,
  AIProvider.mistral,
  AIProvider.venice,
  AIProvider.openAICompatible,
  AIProvider.appleIntelligence,
  AIProvider.mlx,
  AIProvider.aiEdge
];
function isAIProvider(value) {
  return ALL_PROVIDERS.includes(value);
}
function providerDisplayName(provider) {
  return PROVIDER_DISPLAY_NAME[provider];
}
var PROVIDER_DISPLAY_NAME = {
  meta: "Meta (Muse)",
  anthropic: "Anthropic (Claude)",
  openai: "OpenAI (GPT)",
  gemini: "Google Gemini",
  xai: "xAI (Grok)",
  perplexity: "Perplexity (Sonar)",
  deepseek: "DeepSeek",
  zai: "Z.ai (GLM)",
  minimax: "MiniMax",
  mistral: "Mistral AI",
  venice: "Venice AI",
  openAICompatible: "OpenAI Compatible",
  appleIntelligence: "Apple Intelligence",
  localMLX: "MLX (Local)",
  aiEdge: "AI Edge (Local)"
};
function providerBaseURL(provider) {
  return PROVIDER_BASE_URL[provider];
}
var PROVIDER_BASE_URL = {
  meta: "https://api.meta.ai/v1",
  anthropic: "https://api.anthropic.com/v1",
  openai: "https://api.openai.com/v1",
  gemini: "https://generativelanguage.googleapis.com/v1beta",
  xai: "https://api.x.ai/v1",
  perplexity: "https://api.perplexity.ai",
  deepseek: "https://api.deepseek.com",
  zai: "https://api.z.ai/api/paas/v4",
  minimax: "https://api.minimax.io/v1",
  mistral: "https://api.mistral.ai/v1",
  venice: "https://api.venice.ai/api/v1",
  openAICompatible: "",
  // Configured at runtime via OpenAICompatibleSettings
  appleIntelligence: "",
  // On-device, no API URL
  localMLX: "",
  // On-device, no API URL
  aiEdge: ""
  // On-device, no API URL
};
function providerIsOnDevice(provider) {
  return provider === AIProvider.appleIntelligence || provider === AIProvider.mlx || provider === AIProvider.aiEdge;
}
function providerRequiresAPIKey(provider) {
  return !providerIsOnDevice(provider);
}
function providerApiKeyPlaceholder(provider) {
  return PROVIDER_API_KEY_PLACEHOLDER[provider];
}
var PROVIDER_API_KEY_PLACEHOLDER = {
  meta: "LLM|...",
  openai: "sk-...",
  anthropic: "sk-ant-...",
  gemini: "AIza...",
  xai: "xai-...",
  perplexity: "pplx-...",
  deepseek: "sk-...",
  zai: "...",
  minimax: "...",
  mistral: "...",
  venice: "...",
  openAICompatible: "...",
  appleIntelligence: null,
  localMLX: null,
  aiEdge: null
};
function providerCredentialInfoURL(provider) {
  return PROVIDER_CREDENTIAL_INFO_URL[provider];
}
var PROVIDER_CREDENTIAL_INFO_URL = {
  meta: "https://dev.meta.ai/docs/authentication",
  openai: "https://platform.openai.com/account/api-keys",
  anthropic: "https://console.anthropic.com/account/keys",
  gemini: "https://aistudio.google.com/app/apikey",
  xai: "https://console.x.ai",
  perplexity: "https://www.perplexity.ai/settings/api",
  deepseek: "https://platform.deepseek.com/api_keys",
  zai: "https://bigmodel.cn/usercenter/apikeys",
  minimax: "https://platform.minimax.io/user-center/basic-information/interface-key",
  mistral: "https://console.mistral.ai/api-keys",
  venice: "https://venice.ai/settings/api",
  openAICompatible: null,
  appleIntelligence: null,
  localMLX: null,
  aiEdge: null
};
function providerCredentialDescription(provider) {
  return PROVIDER_CREDENTIAL_DESCRIPTION[provider];
}
var PROVIDER_CREDENTIAL_DESCRIPTION = {
  meta: "Required for Muse Spark models",
  openai: "Required for GPT models",
  anthropic: "Required for Claude models",
  gemini: "Required for Gemini models",
  xai: "Required for Grok models",
  perplexity: "Required for Sonar models (online search)",
  deepseek: "Required for DeepSeek models",
  zai: "Required for GLM models",
  minimax: "Required for MiniMax M-series models",
  mistral: "Required for Mistral and Pixtral models",
  venice: "Required for Venice AI models",
  openAICompatible: "Required if your OpenAI-compatible endpoint enforces authentication",
  appleIntelligence: "Runs on device through Apple Intelligence",
  localMLX: "Runs locally with MLX",
  aiEdge: "Runs locally with AI Edge"
};
function providerIsOpenAICompatible(provider) {
  switch (provider) {
    case AIProvider.anthropic:
    case AIProvider.gemini:
    case AIProvider.appleIntelligence:
    case AIProvider.mlx:
    case AIProvider.aiEdge:
      return false;
    default:
      return true;
  }
}
function providerSupportsExternalToolCalling(provider, _model) {
  switch (provider) {
    case AIProvider.appleIntelligence:
    case AIProvider.mlx:
    case AIProvider.aiEdge:
      return false;
    default:
      return true;
  }
}
function providerSupportsStreaming(_provider) {
  return true;
}
function providerIsAvailable(provider) {
  switch (provider) {
    case AIProvider.mlx:
    case AIProvider.aiEdge:
      return onDeviceAvailability[provider].available;
    default:
      return true;
  }
}
function providerUnavailabilityReason(provider) {
  switch (provider) {
    case AIProvider.mlx:
    case AIProvider.aiEdge:
      return onDeviceAvailability[provider].available ? null : onDeviceAvailability[provider].reason;
    default:
      return null;
  }
}
var onDeviceAvailability = {
  localMLX: {
    available: false,
    reason: "MLX inference is not available in this runtime; register a custom provider handler."
  },
  aiEdge: {
    available: false,
    reason: "AI Edge inference is not available in this runtime; register a custom provider handler."
  }
};
function setOnDeviceAvailability(provider, available, reason = null) {
  onDeviceAvailability[provider] = {
    available,
    reason: available ? null : reason ?? onDeviceAvailability[provider].reason
  };
}
function providerFromCompatibleRawValue(rawValue) {
  switch (rawValue) {
    case "appleFoundation":
      return AIProvider.appleIntelligence;
    case "localMLX":
      return AIProvider.mlx;
    default:
      return isAIProvider(rawValue) ? rawValue : null;
  }
}
function axonCompatibleRawValue(provider) {
  switch (provider) {
    case AIProvider.appleIntelligence:
      return "appleFoundation";
    case AIProvider.mlx:
      return "localMLX";
    default:
      return provider;
  }
}
function modelSupportsImages(model) {
  return model.modalities.includes("image");
}
function modelSupportsAudio(model) {
  return model.modalities.includes("audio");
}
function modelSupportsVideo(model) {
  return model.modalities.includes("video");
}
function modelSupportsDocuments(model) {
  return model.modalities.includes("document") || model.modalities.includes("pdf");
}

// src/core/models/openai-responses-routing.ts
var FAMILIES = [
  "gpt-5.3-codex",
  "gpt-5.6",
  "gpt-5.6-sol",
  "gpt-5.6-terra",
  "gpt-5.6-luna",
  "gpt-6-astra",
  "gpt-6.1-sol",
  "gpt-6-sol",
  "gpt-6-luna"
];
var DATED_SNAPSHOT = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
function family(model) {
  for (const candidate of FAMILIES) {
    if (model === candidate) return candidate;
    if (!model.startsWith(`${candidate}-`)) continue;
    if (DATED_SNAPSHOT.test(model.slice(candidate.length + 1))) return candidate;
  }
  return null;
}
var OpenAIResponsesRouting = {
  usesResponses(model) {
    return family(model) !== null;
  },
  supportedReasoningEfforts(model) {
    const resolved = family(model);
    if (resolved === null) return null;
    if (resolved === "gpt-5.3-codex") return ["low", "medium", "high", "xhigh"];
    const reasoning = ["low", "medium", "high", "xhigh", "max"];
    return ["gpt-6-astra", "gpt-6.1-sol"].includes(resolved) ? reasoning : ["none", ...reasoning];
  },
  normalizedReasoningEffort(effort, model) {
    const resolved = family(model);
    if ((resolved === "gpt-6-astra" || resolved === "gpt-6.1-sol" || resolved === "gpt-5.3-codex") && effort === "none") {
      return "low";
    }
    if (resolved === "gpt-5.3-codex" && effort === "max") return "xhigh";
    return effort ?? null;
  }
};

// src/core/utilities/uuid.ts
function generateUUID() {
  const cryptoObj = globalThis.crypto;
  if (cryptoObj?.randomUUID) {
    return cryptoObj.randomUUID().toUpperCase();
  }
  if (cryptoObj?.getRandomValues) {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
    bytes[6] = bytes[6] & 15 | 64;
    bytes[8] = bytes[8] & 63 | 128;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`.toUpperCase();
  }
  let uuid = "";
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      uuid += "-";
    } else if (i === 14) {
      uuid += "4";
    } else {
      const r = Math.random() * 16 | 0;
      uuid += (i === 19 ? r & 3 | 8 : r).toString(16);
    }
  }
  return uuid.toUpperCase();
}

// src/core/models/message.ts
function tokenUsage(input, output, total, cachedInput, cacheCreationInput) {
  return {
    input,
    output,
    total: total ?? input + output,
    ...cachedInput !== void 0 && { cachedInput },
    ...cacheCreationInput !== void 0 && { cacheCreationInput }
  };
}
function providerMetadataIsEmpty(metadata) {
  return (!metadata.citations || metadata.citations.length === 0) && !metadata.responsesOutputJSON && !metadata.anthropicContentJSON && !metadata.appleResponseTranscriptJSON;
}
function createAttachment(init) {
  return {
    id: init.id ?? generateUUID(),
    type: init.type,
    url: init.url,
    base64: init.base64,
    name: init.name,
    mimeType: init.mimeType
  };
}
var MessageAttachments = {
  imageBase64(base64, mimeType = "image/jpeg") {
    return createAttachment({ type: "image", base64, mimeType });
  },
  imageURL(url) {
    return createAttachment({ type: "image", url });
  },
  document(base64, name, mimeType = "application/pdf") {
    return createAttachment({ type: "document", base64, name, mimeType });
  },
  audioBase64(base64, mimeType = "audio/mpeg") {
    return createAttachment({ type: "audio", base64, mimeType });
  },
  audioURL(url, mimeType = "audio/mpeg") {
    return createAttachment({ type: "audio", url, mimeType });
  }
};
function createMessage(init) {
  return {
    id: init.id ?? generateUUID(),
    role: init.role,
    content: init.content,
    timestamp: init.timestamp ?? /* @__PURE__ */ new Date(),
    tokens: init.tokens,
    modelName: init.modelName,
    providerName: init.providerName,
    attachments: init.attachments,
    reasoning: init.reasoning,
    toolCalls: init.toolCalls,
    toolCallId: init.toolCallId,
    toolName: init.toolName,
    providerMetadata: init.providerMetadata
  };
}
function toolResultMessage(toolCallId, toolName, content) {
  return createMessage({ role: "tool", content, toolCallId, toolName });
}
function messageHasToolCalls(message) {
  return !!message.toolCalls && message.toolCalls.length > 0;
}
function messageIsToolResult(message) {
  return message.role === "tool" && message.toolCallId != null;
}
function messageToJSON(message) {
  const json = {
    id: message.id,
    role: message.role,
    content: message.content,
    timestamp: message.timestamp.toISOString()
  };
  if (message.tokens !== void 0) json.tokens = message.tokens;
  if (message.modelName !== void 0) json.modelName = message.modelName;
  if (message.providerName !== void 0) json.providerName = message.providerName;
  if (message.attachments !== void 0) json.attachments = [...message.attachments];
  if (message.reasoning !== void 0) json.reasoning = message.reasoning;
  if (message.toolCalls !== void 0) json.toolCalls = [...message.toolCalls];
  if (message.toolCallId !== void 0) json.toolCallId = message.toolCallId;
  if (message.toolName !== void 0) json.toolName = message.toolName;
  if (message.providerMetadata !== void 0) json.providerMetadata = message.providerMetadata;
  return json;
}
function messageFromJSON(json) {
  return {
    id: json.id,
    role: json.role,
    content: json.content,
    timestamp: new Date(json.timestamp),
    tokens: json.tokens,
    modelName: json.modelName,
    providerName: json.providerName,
    attachments: json.attachments,
    reasoning: json.reasoning,
    toolCalls: json.toolCalls,
    toolCallId: json.toolCallId,
    toolName: json.toolName,
    providerMetadata: json.providerMetadata
  };
}
function mimeTypeForPath(path) {
  const ext = extensionOf(path);
  switch (ext) {
    // Documents
    case "pdf":
      return "application/pdf";
    case "txt":
    case "text":
      return "text/plain";
    case "json":
      return "application/json";
    case "xml":
      return "application/xml";
    // Images
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "heic":
      return "image/heic";
    // Video
    case "mp4":
    case "m4v":
      return "video/mp4";
    case "mov":
      return "video/quicktime";
    case "avi":
      return "video/avi";
    case "webm":
      return "video/webm";
    // Audio
    case "wav":
      return "audio/wav";
    case "mp3":
      return "audio/mp3";
    case "m4a":
    case "aac":
      return "audio/aac";
    case "ogg":
      return "audio/ogg";
    case "flac":
      return "audio/flac";
    default:
      return "application/octet-stream";
  }
}
function attachmentTypeForPath(path) {
  const ext = extensionOf(path);
  if (["mp4", "m4v", "mov", "avi", "webm", "wmv", "flv"].includes(ext)) {
    return "video";
  }
  if (["wav", "mp3", "m4a", "aac", "ogg", "flac", "aiff"].includes(ext)) {
    return "audio";
  }
  if (["jpg", "jpeg", "png", "gif", "webp", "heic", "heif"].includes(ext)) {
    return "image";
  }
  return "document";
}
function extensionOf(path) {
  const withoutQuery = path.split(/[?#]/, 1)[0] ?? path;
  const lastSegment = withoutQuery.split("/").pop() ?? "";
  const dotIndex = lastSegment.lastIndexOf(".");
  if (dotIndex <= 0) return "";
  return lastSegment.slice(dotIndex + 1).toLowerCase();
}

// src/core/models/tool.ts
function toolParameters(properties, required = []) {
  return { type: "object", properties, required };
}
function parseToolCallArguments(toolCall) {
  try {
    const parsed = JSON.parse(toolCall.arguments);
    return isJSONObject(parsed) ? parsed : null;
  } catch {
    return null;
  }
}
var ToolChoice = {
  auto: { type: "auto" },
  required: { type: "required" },
  none: { type: "none" },
  specific(toolName) {
    return { type: "specific", toolName };
  }
};
function createPartialToolCall(index) {
  return { index, id: "", name: "", arguments: "" };
}
function partialToToolCall(partial) {
  return { id: partial.id, name: partial.name, arguments: partial.arguments };
}

// src/core/models/streaming-types.ts
var StreamingError = class _StreamingError extends Error {
  kind;
  /// HTTP status code for `providerError`.
  statusCode;
  constructor(kind, message, statusCode) {
    super(message);
    this.name = "StreamingError";
    this.kind = kind;
    this.statusCode = statusCode;
  }
  /// Failed to establish connection.
  static connectionFailed(message) {
    return new _StreamingError("connectionFailed", `Connection failed: ${message}`);
  }
  /// Provider returned an error.
  static providerError(code, message) {
    return new _StreamingError("providerError", `Provider error (${code}): ${message}`, code);
  }
  /// Failed to parse response data.
  static parseError(message) {
    return new _StreamingError("parseError", `Parse error: ${message}`);
  }
  /// Request timed out.
  static timeout() {
    return new _StreamingError("timeout", "Request timed out");
  }
  /// Request was cancelled.
  static cancelled() {
    return new _StreamingError("cancelled", "Request cancelled");
  }
  /// Provider does not support streaming.
  static unsupportedProvider(provider) {
    return new _StreamingError(
      "unsupportedProvider",
      `Streaming not supported for provider: ${provider}`
    );
  }
};
var StreamingEvents = {
  textDelta(text) {
    return { type: "textDelta", text };
  },
  reasoningDelta(text) {
    return { type: "reasoningDelta", text };
  },
  completion(completion) {
    return { type: "completion", completion };
  },
  error(error) {
    return { type: "error", error };
  },
  toolCallStart(toolCall) {
    return { type: "toolCallStart", toolCall };
  },
  toolCallDelta(id, argumentsDelta) {
    return { type: "toolCallDelta", id, argumentsDelta };
  },
  toolCallComplete(toolCall) {
    return { type: "toolCallComplete", toolCall };
  },
  toolResultRequired(toolCalls) {
    return { type: "toolResultRequired", toolCalls };
  }
};
function createStreamingConfig(init) {
  return {
    provider: init.provider,
    apiKey: init.apiKey,
    model: init.model,
    baseUrl: init.baseUrl,
    system: init.system,
    maxTokens: init.maxTokens ?? 4096,
    settings: init.settings,
    geminiCapabilities: init.geminiCapabilities,
    tools: init.tools,
    toolChoice: init.toolChoice,
    useStreaming: init.useStreaming ?? true,
    promptCacheKey: init.promptCacheKey
  };
}

// src/core/models/model-settings.ts
var AnthropicSettings = class {
  /// Controls randomness in generation. Range: 0.0 to 1.0 (default: 1.0).
  temperature;
  /// Nucleus sampling threshold. Range: 0.0 to 1.0 (default: 1.0).
  topP;
  /// Sample from top K most likely tokens. Range: 1 to 1000.
  topK;
  constructor(init = {}) {
    this.temperature = init.temperature;
    this.topP = init.topP;
    this.topK = init.topK;
  }
  toRequestParameters() {
    const params = {};
    if (this.temperature !== void 0) params["temperature"] = this.temperature;
    if (this.topP !== void 0) params["top_p"] = this.topP;
    if (this.topK !== void 0) params["top_k"] = this.topK;
    return params;
  }
};
var OPENAI_REASONING_EFFORTS = [
  "none",
  "low",
  "medium",
  "high",
  "xhigh",
  "max"
];
function isOpenAIReasoningEffort(value) {
  return OPENAI_REASONING_EFFORTS.includes(value);
}
var OpenAISettings = class {
  /// Controls randomness in generation. Range: 0.0 to 2.0 (default: 1.0).
  temperature;
  /// Nucleus sampling threshold. Range: 0.0 to 1.0 (default: 1.0).
  topP;
  /// Penalize tokens based on their frequency in the text so far. Range: -2.0 to 2.0.
  frequencyPenalty;
  /// Penalize tokens based on whether they appear in the text so far. Range: -2.0 to 2.0.
  presencePenalty;
  /// Sequences that will stop generation (up to 4).
  stopSequences;
  /// Reasoning effort for OpenAI models that support configurable reasoning.
  reasoningEffort;
  constructor(init = {}) {
    this.temperature = init.temperature;
    this.topP = init.topP;
    this.frequencyPenalty = init.frequencyPenalty;
    this.presencePenalty = init.presencePenalty;
    this.stopSequences = init.stopSequences;
    this.reasoningEffort = init.reasoningEffort;
  }
  toRequestParameters() {
    const params = {};
    if (this.temperature !== void 0) params["temperature"] = this.temperature;
    if (this.topP !== void 0) params["top_p"] = this.topP;
    if (this.frequencyPenalty !== void 0) params["frequency_penalty"] = this.frequencyPenalty;
    if (this.presencePenalty !== void 0) params["presence_penalty"] = this.presencePenalty;
    if (this.stopSequences && this.stopSequences.length > 0) params["stop"] = this.stopSequences;
    if (this.reasoningEffort !== void 0) params["reasoning_effort"] = this.reasoningEffort;
    return params;
  }
};
var GrokReasoningSettings = class {
  /// Base OpenAI-compatible settings.
  base;
  /// Controls how hard the model thinks (supported by grok-3-mini).
  reasoningEffort;
  /// Request encrypted reasoning content (supported by grok-4).
  useEncryptedContent;
  constructor(init = {}) {
    this.base = init.base ?? new OpenAISettings({ temperature: init.temperature, topP: init.topP });
    this.reasoningEffort = init.reasoningEffort;
    this.useEncryptedContent = init.useEncryptedContent;
  }
  toRequestParameters() {
    const params = this.base.toRequestParameters();
    if (this.reasoningEffort !== void 0) params["reasoning_effort"] = this.reasoningEffort;
    if (this.useEncryptedContent !== void 0) {
      params["use_encrypted_content"] = this.useEncryptedContent;
    }
    return params;
  }
};
var MUSE_REASONING_EFFORTS = [
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max"
];
function isMuseReasoningEffort(value) {
  return MUSE_REASONING_EFFORTS.includes(value);
}
function supportedMuseReasoningEfforts(model) {
  return model === "muse-spark-1.3" ? [...MUSE_REASONING_EFFORTS] : MUSE_REASONING_EFFORTS.filter((effort) => effort !== "max");
}
var MetaSettings = class {
  reasoningEffort;
  constructor(init = {}) {
    this.reasoningEffort = init.reasoningEffort;
  }
  toRequestParameters() {
    return this.reasoningEffort !== void 0 ? { reasoning_effort: this.reasoningEffort } : {};
  }
};
var GEMINI_THINKING_LEVELS = [
  "dynamic",
  "low",
  "medium",
  "high"
];
var GeminiSettings = class {
  /// Controls randomness in generation. Range: 0.0 to 2.0 (default: 1.0).
  temperature;
  /// Nucleus sampling threshold. Range: 0.0 to 1.0.
  topP;
  /// Sample from top K most likely tokens.
  topK;
  /// Sequences that will stop generation.
  stopSequences;
  /**
   * Token budget for Gemini's thinking/reasoning, nested under
   * `generationConfig.thinkingConfig.thinkingBudget`.
   *
   * Applies to Gemini 2.5 Pro/Flash and Gemini 3. A value of `0` disables
   * thinking — but only Flash variants honor `0`; Pro models may ignore or
   * reject it. See https://ai.google.dev/gemini-api/docs/thinking
   */
  thinkingBudget;
  /**
   * Fixed thinking effort for Gemini 3 models, nested under
   * `generationConfig.thinkingConfig.thinkingLevel`.
   *
   * Ignored by Gemini 2.5. Prefer `thinkingBudget` for 2.5, `thinkingLevel` for 3.
   */
  thinkingLevel;
  constructor(init = {}) {
    this.temperature = init.temperature;
    this.topP = init.topP;
    this.topK = init.topK;
    this.stopSequences = init.stopSequences;
    this.thinkingBudget = init.thinkingBudget;
    this.thinkingLevel = init.thinkingLevel;
  }
  toRequestParameters() {
    const params = {};
    if (this.temperature !== void 0) params["temperature"] = this.temperature;
    if (this.topP !== void 0) params["topP"] = this.topP;
    if (this.topK !== void 0) params["topK"] = this.topK;
    if (this.stopSequences && this.stopSequences.length > 0) {
      params["stopSequences"] = this.stopSequences;
    }
    const thinkingConfig = {};
    if (this.thinkingBudget !== void 0) thinkingConfig["thinkingBudget"] = this.thinkingBudget;
    if (this.thinkingLevel !== void 0) thinkingConfig["thinkingLevel"] = this.thinkingLevel;
    if (Object.keys(thinkingConfig).length > 0) {
      params["thinkingConfig"] = thinkingConfig;
    }
    return params;
  }
};
var PerplexitySettings = class {
  /// Base OpenAI-compatible settings.
  base;
  /// Filter search results to specific domains.
  searchDomainFilter;
  /// Whether to include images in the response.
  returnImages;
  /// Whether to return related questions.
  returnRelatedQuestions;
  constructor(init = {}) {
    this.base = init.base ?? new OpenAISettings({ temperature: init.temperature, topP: init.topP });
    this.searchDomainFilter = init.searchDomainFilter;
    this.returnImages = init.returnImages;
    this.returnRelatedQuestions = init.returnRelatedQuestions;
  }
  toRequestParameters() {
    const params = this.base.toRequestParameters();
    Object.assign(params, this.toPerplexityParams() ?? {});
    return params;
  }
  /**
   * Returns only the Perplexity-specific parameters (not base OpenAI settings).
   * Used internally when base settings are applied separately.
   */
  toPerplexityParams() {
    const params = {};
    if (this.searchDomainFilter && this.searchDomainFilter.length > 0) {
      params["search_domain_filter"] = this.searchDomainFilter;
    }
    if (this.returnImages !== void 0) params["return_images"] = this.returnImages;
    if (this.returnRelatedQuestions !== void 0) {
      params["return_related_questions"] = this.returnRelatedQuestions;
    }
    return Object.keys(params).length > 0 ? params : null;
  }
};
var ZaiSettings = class {
  /// Base OpenAI-compatible settings.
  base;
  /// Enable extended thinking mode (chain-of-thought reasoning for complex tasks).
  thinkingEnabled;
  constructor(init = {}) {
    this.base = init.base ?? new OpenAISettings({ temperature: init.temperature, topP: init.topP });
    this.thinkingEnabled = init.thinkingEnabled;
  }
  toRequestParameters() {
    const params = this.base.toRequestParameters();
    if (this.thinkingEnabled !== void 0) {
      params["thinking"] = { type: this.thinkingEnabled ? "enabled" : "disabled" };
    }
    return params;
  }
};
var MiniMaxSettings = class {
  /// Controls randomness in generation.
  temperature;
  /// Nucleus sampling parameter.
  topP;
  /// Request admission tier.
  serviceTier;
  /// Controls MiniMax M3 thinking behavior.
  thinking;
  /// Separates thinking content into reasoning fields when supported.
  reasoningSplit;
  constructor(init = {}) {
    this.temperature = init.temperature;
    this.topP = init.topP;
    this.serviceTier = init.serviceTier;
    this.thinking = init.thinking;
    this.reasoningSplit = init.reasoningSplit;
  }
  toRequestParameters() {
    const params = {};
    if (this.temperature !== void 0) params["temperature"] = this.temperature;
    if (this.topP !== void 0) params["top_p"] = this.topP;
    if (this.serviceTier !== void 0) params["service_tier"] = this.serviceTier;
    if (this.thinking !== void 0) params["thinking"] = { type: this.thinking };
    if (this.reasoningSplit !== void 0) params["reasoning_split"] = this.reasoningSplit;
    return params;
  }
};
var VeniceSettings = class {
  /// Base OpenAI-compatible settings.
  base;
  /// Controls web search behavior.
  enableWebSearch;
  /// Whether to include web citations (emitted in `[REF]0[/REF]`-style markup).
  enableWebCitations;
  /// Whether to scrape URLs in user messages and inject as context.
  enableWebScraping;
  /// Whether to include search results as the first streamed chunk.
  includeSearchResultsInStream;
  /// Route through a published Venice character.
  characterSlug;
  /// Strip thinking/reasoning traces from the response.
  stripThinkingResponse;
  /// Disable extended thinking mode for reasoning models.
  disableThinking;
  /// Include Venice's default uncensored system prompt (defaults to true server-side).
  includeVeniceSystemPrompt;
  /// A routing hint to co-locate related conversations for higher cache hit rates.
  promptCacheKey;
  /// Prompt cache retention policy.
  promptCacheRetention;
  /// Reasoning effort level for supported models.
  reasoningEffort;
  constructor(init = {}) {
    this.base = init.base ?? new OpenAISettings({ temperature: init.temperature, topP: init.topP });
    this.enableWebSearch = init.enableWebSearch;
    this.enableWebCitations = init.enableWebCitations;
    this.enableWebScraping = init.enableWebScraping;
    this.includeSearchResultsInStream = init.includeSearchResultsInStream;
    this.characterSlug = init.characterSlug;
    this.stripThinkingResponse = init.stripThinkingResponse;
    this.disableThinking = init.disableThinking;
    this.includeVeniceSystemPrompt = init.includeVeniceSystemPrompt;
    this.promptCacheKey = init.promptCacheKey;
    this.promptCacheRetention = init.promptCacheRetention;
    this.reasoningEffort = init.reasoningEffort;
  }
  toRequestParameters() {
    const params = this.base.toRequestParameters();
    const veniceParams = {};
    if (this.enableWebSearch !== void 0) {
      veniceParams["enable_web_search"] = this.enableWebSearch;
    }
    if (this.enableWebCitations !== void 0) {
      veniceParams["enable_web_citations"] = this.enableWebCitations;
    }
    if (this.enableWebScraping !== void 0) {
      veniceParams["enable_web_scraping"] = this.enableWebScraping;
    }
    if (this.includeSearchResultsInStream !== void 0) {
      veniceParams["include_search_results_in_stream"] = this.includeSearchResultsInStream;
    }
    if (this.characterSlug !== void 0) veniceParams["character_slug"] = this.characterSlug;
    if (this.stripThinkingResponse !== void 0) {
      veniceParams["strip_thinking_response"] = this.stripThinkingResponse;
    }
    if (this.disableThinking !== void 0) {
      veniceParams["disable_thinking"] = this.disableThinking;
    }
    if (this.includeVeniceSystemPrompt !== void 0) {
      veniceParams["include_venice_system_prompt"] = this.includeVeniceSystemPrompt;
    }
    if (Object.keys(veniceParams).length > 0) {
      params["venice_parameters"] = veniceParams;
    }
    if (this.promptCacheKey !== void 0) params["prompt_cache_key"] = this.promptCacheKey;
    if (this.promptCacheRetention !== void 0) {
      params["prompt_cache_retention"] = this.promptCacheRetention;
    }
    if (this.reasoningEffort !== void 0) params["reasoning_effort"] = this.reasoningEffort;
    return params;
  }
  /**
   * Returns only the Venice-specific parameters (not base OpenAI settings).
   * Used internally when base settings are applied separately.
   */
  toVeniceParams() {
    const params = this.toRequestParameters();
    for (const key of Object.keys(this.base.toRequestParameters())) {
      delete params[key];
    }
    return Object.keys(params).length > 0 ? params : null;
  }
};

// src/core/models/access-policy.ts
var ModelAccess = {
  all: { type: "all" },
  only(modelIds) {
    return { type: "only", modelIds: new Set(modelIds) };
  }
};
var AIProviderAccessPolicy = class _AIProviderAccessPolicy {
  /// Policy that preserves the default package behavior.
  static all = new _AIProviderAccessPolicy(null);
  rules;
  constructor(rules) {
    this.rules = rules;
  }
  /// Allow every model for the listed providers.
  static allowingProviders(providers) {
    const rules = /* @__PURE__ */ new Map();
    for (const provider of providers) {
      rules.set(provider, ModelAccess.all);
    }
    return new _AIProviderAccessPolicy(rules);
  }
  /**
   * Allow providers and model IDs according to explicit rules.
   *
   * Passing an empty map creates a policy that allows no built-in providers.
   */
  static allowing(rules) {
    const map = rules instanceof Map ? new Map(rules) : new Map(
      Object.entries(rules)
    );
    return new _AIProviderAccessPolicy(map);
  }
  /// Whether the provider is allowed by this policy.
  allowsProvider(provider) {
    if (this.rules === null) return true;
    return this.rules.has(provider);
  }
  /// Whether the provider/model pair is allowed by this policy.
  allowsModel(provider, model) {
    if (this.rules === null) return true;
    const modelAccess = this.rules.get(provider);
    if (!modelAccess) return false;
    switch (modelAccess.type) {
      case "all":
        return true;
      case "only":
        return modelAccess.modelIds.has(model);
    }
  }
  /// Providers visible to model pickers under this policy.
  get availableProviders() {
    return ALL_PROVIDERS.filter((provider) => this.allowsProvider(provider));
  }
};

// src/core/protocols/custom-provider-handler.ts
async function* streamWithHandler(handler, request) {
  if (handler.stream) {
    yield* handler.stream(request);
    return;
  }
  yield StreamingEvents.error(StreamingError.unsupportedProvider(handler.providerId));
}

// src/core/errors/api-error.ts
var APIError = class _APIError extends Error {
  kind;
  /// HTTP status code carried by `serverError`.
  code;
  /// Seconds to wait before retrying, for `rateLimited`.
  retryAfter;
  /// The provider-supplied message, when one was parsed.
  providerMessage;
  constructor(kind, message, options = {}) {
    super(message);
    this.name = "APIError";
    this.kind = kind;
    this.code = options.code;
    this.retryAfter = options.retryAfter;
    this.providerMessage = options.providerMessage;
  }
  /// Missing or invalid API key.
  static unauthorized(message) {
    return new _APIError(
      "unauthorized",
      message ? `Unauthorized: ${message}` : "Unauthorized: Missing or invalid API key",
      message ? { providerMessage: message } : {}
    );
  }
  /// Server returned an error status code with optional message from provider.
  static serverError(code, message) {
    return new _APIError(
      "serverError",
      message ? `Server error (HTTP ${code}): ${message}` : `Server error: HTTP ${code}`,
      message ? { code, providerMessage: message } : { code }
    );
  }
  /// Network request failed.
  static networkError(message) {
    return new _APIError("networkError", `Network error: ${message}`);
  }
  /// Failed to parse response.
  static parseError(message) {
    return new _APIError("parseError", `Parse error: ${message}`);
  }
  /// Invalid request configuration.
  static invalidRequest(message) {
    return new _APIError("invalidRequest", `Invalid request: ${message}`);
  }
  /// Provider not supported.
  static unsupportedProvider(provider) {
    return new _APIError("unsupportedProvider", `Unsupported provider: ${provider}`);
  }
  /// Rate limit exceeded.
  static rateLimited(retryAfter, message) {
    let result = "Rate limited";
    if (retryAfter != null) result += `. Retry after ${retryAfter} seconds`;
    if (message) result += `: ${message}`;
    const options = {};
    if (retryAfter != null) options.retryAfter = retryAfter;
    if (message) options.providerMessage = message;
    return new _APIError("rateLimited", result, options);
  }
  /// Request was cancelled.
  static cancelled() {
    return new _APIError("cancelled", "Request cancelled");
  }
  /// HTTP status code if available.
  get statusCode() {
    switch (this.kind) {
      case "serverError":
        return this.code ?? null;
      case "unauthorized":
        return 401;
      case "rateLimited":
        return 429;
      default:
        return null;
    }
  }
};
var ErrorResponseParser = {
  /**
   * Parse an error response body (raw text) into structured error information.
   * Handles OpenAI/Anthropic/Gemini/MiniMax/Z.ai/FastAPI error shapes.
   */
  parse(body) {
    let jsonValue;
    try {
      jsonValue = JSON.parse(body);
    } catch {
      return body.length > 0 ? { message: body } : null;
    }
    if (isRecord(jsonValue)) {
      const parsed = parseObject(jsonValue);
      if (parsed) return parsed;
    } else if (Array.isArray(jsonValue)) {
      for (const entry of jsonValue) {
        if (isRecord(entry)) {
          const parsed = parseObject(entry);
          if (parsed) return parsed;
        }
      }
    }
    return { message: body };
  },
  /// Parse an error response body into a human-readable message (legacy method).
  parseErrorBody(body) {
    return this.parse(body)?.message ?? null;
  },
  /// Create an appropriate APIError from HTTP status code and response body.
  makeError(statusCode, body) {
    const parsed = this.parse(body);
    const message = parsed?.message ?? null;
    switch (statusCode) {
      case 401:
        return APIError.unauthorized(message);
      case 429:
        return APIError.rateLimited(parsed?.retryAfter ?? null, message);
      case 400:
        return APIError.invalidRequest(message ?? "Bad request");
      default:
        return APIError.serverError(statusCode, message);
    }
  }
};
function isRecord(value) {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function parseObject(json) {
  const error = json["error"];
  if (isRecord(error) && typeof error["message"] === "string") {
    const type = typeof error["type"] === "string" ? error["type"] : typeof error["status"] === "string" ? error["status"] : void 0;
    const code = typeof error["code"] === "string" ? error["code"] : typeof error["code"] === "number" ? String(error["code"]) : void 0;
    return { message: error["message"], type, code };
  }
  const baseResp = json["base_resp"];
  if (isRecord(baseResp) && typeof baseResp["status_msg"] === "string") {
    const statusCode = baseResp["status_code"];
    return {
      message: baseResp["status_msg"],
      code: typeof statusCode === "number" ? String(statusCode) : void 0
    };
  }
  if (typeof json["msg"] === "string") {
    const code = json["code"];
    return {
      message: json["msg"],
      code: typeof code === "string" ? code : typeof code === "number" ? String(code) : void 0
    };
  }
  if (typeof json["message"] === "string") {
    return { message: json["message"] };
  }
  if (typeof json["detail"] === "string") {
    return { message: json["detail"] };
  }
  return null;
}

// src/core/extensions/openai-compatible/settings.ts
var OpenAICompatibleSettings = class {
  /// Base URL for the OpenAI-compatible server (e.g., "http://localhost:1234/v1").
  baseUrl;
  /**
   * API key for authentication.
   *
   * Many local servers accept any string (e.g., "lm-studio", "not-needed").
   * Set to an empty string if the server requires no authentication.
   */
  apiKey;
  /// Standard OpenAI-compatible generation parameters.
  base;
  /**
   * Whether to use streaming for responses. Some servers don't support SSE
   * streaming. When `false`, the full response is fetched in a single request.
   */
  useStreaming;
  constructor(init = {}) {
    this.baseUrl = init.baseUrl ?? "http://localhost:1234/v1";
    this.apiKey = init.apiKey ?? "lm-studio";
    this.useStreaming = init.useStreaming ?? true;
    this.base = init.base ?? new OpenAISettings({
      temperature: init.temperature,
      topP: init.topP,
      frequencyPenalty: init.frequencyPenalty,
      presencePenalty: init.presencePenalty,
      stopSequences: init.stopSequences
    });
  }
  toRequestParameters() {
    return this.base.toRequestParameters();
  }
};

export { AIProvider, AIProviderAccessPolicy, ALL_PROVIDERS, APIError, AnthropicSettings, ErrorResponseParser, GEMINI_THINKING_LEVELS, GeminiSettings, GrokReasoningSettings, MUSE_REASONING_EFFORTS, MessageAttachments, MetaSettings, MiniMaxSettings, ModelAccess, OPENAI_REASONING_EFFORTS, OpenAICompatibleSettings, OpenAIResponsesRouting, OpenAISettings, PerplexitySettings, StreamingError, StreamingEvents, ToolChoice, VeniceSettings, ZaiSettings, attachmentTypeForPath, axonCompatibleRawValue, createAttachment, createMessage, createPartialToolCall, createStreamingConfig, generateUUID, isAIProvider, isJSONObject, isMuseReasoningEffort, isOpenAIReasoningEffort, messageFromJSON, messageHasToolCalls, messageIsToolResult, messageToJSON, mimeTypeForPath, modelSupportsAudio, modelSupportsDocuments, modelSupportsImages, modelSupportsVideo, parseToolCallArguments, partialToToolCall, providerApiKeyPlaceholder, providerBaseURL, providerCredentialDescription, providerCredentialInfoURL, providerDisplayName, providerFromCompatibleRawValue, providerIsAvailable, providerIsOnDevice, providerIsOpenAICompatible, providerMetadataIsEmpty, providerRequiresAPIKey, providerSupportsExternalToolCalling, providerSupportsStreaming, providerUnavailabilityReason, setOnDeviceAvailability, streamWithHandler, supportedMuseReasoningEfforts, tokenUsage, toolParameters, toolResultMessage };
//# sourceMappingURL=chunk-3PUWXRLZ.js.map
//# sourceMappingURL=chunk-3PUWXRLZ.js.map